import { produce } from 'immer'
import { create } from 'zustand'

import { generateId } from 'features/charts-v2/store'
import type { Condition } from 'features/charts-v2/types/yogiql'
import {
  defaultFeedbackQuery,
  type SearchFields,
} from 'features/feedback/feedback-query'
import type { KeywordsQueryPanel } from 'features/keywords/types'

// ---------------------------------------------------------------------------
// Under `enhanced_dialect`, the keywords page's filter state: one YogiQL
// `query` per panel, in place of `useKeywordsFiltersStore`'s v2 panels. Panel
// count follows `usePanelStore.comparativePanelsNumber` the same way, via
// `setPanelCount`. Mirrors feedback's `useFeedbackQueryStore`.
// ---------------------------------------------------------------------------

export type KeywordsQueryState = {
  panels: KeywordsQueryPanel[]
  activePanelId: string | null
  /** Eye-toggled-off conditions per panel, by the v2 field they read as.
   *  UI-only, never saved, as `useKeywordsFiltersStore.ghostFilters` is. */
  ghosts: Record<string, Record<string, Condition[]>>
  /** The search cards the sidebar last set, so an empty card (no condition
   *  yet) stays on screen while it still agrees with the query. UI-only. */
  shownSearch: Record<string, SearchFields>

  hydrate: (state: {
    panels: KeywordsQueryPanel[]
    activePanelId: string | null
  }) => void
  setPanelCount: (n: number) => void
  removePanelAt: (index: number) => void
  setFilters: (panelId: string, filters: Condition[]) => void
  setGhosts: (panelId: string, ghosts: Record<string, Condition[]>) => void
  setShownSearch: (panelId: string, search: SearchFields) => void
  setActivePanelId: (id: string | null) => void
  resetKeywordsQueries: () => void
}

const initialKeywordsQueryState = {
  panels: [] as KeywordsQueryPanel[],
  activePanelId: null as string | null,
  ghosts: {} as Record<string, Record<string, Condition[]>>,
  shownSearch: {} as Record<string, SearchFields>,
}

const dropStale = <T>(
  byPanel: Record<string, T>,
  panels: KeywordsQueryPanel[],
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(byPanel).filter(([id]) => panels.some((p) => p.id === id)),
  )

export const useKeywordsQueryStore = create<KeywordsQueryState>()(
  (set, get) => ({
    ...initialKeywordsQueryState,

    hydrate: ({ panels, activePanelId }) => {
      // A hash pop restores the same panel ids, so their ghosts survive.
      set({
        panels,
        activePanelId,
        ghosts: dropStale(get().ghosts, panels),
        shownSearch: dropStale(get().shownSearch, panels),
      })
    },

    setPanelCount: (n) => {
      set(
        produce((state: KeywordsQueryState) => {
          if (n === state.panels.length) return
          if (n < state.panels.length) {
            state.panels = state.panels.slice(0, Math.max(n, 0))
            state.ghosts = dropStale(state.ghosts, state.panels)
            state.shownSearch = dropStale(state.shownSearch, state.panels)
            if (!state.panels.some((p) => p.id === state.activePanelId)) {
              state.activePanelId = state.panels[0]?.id ?? null
            }
            return
          }
          const seed = state.panels[0]?.query
          while (state.panels.length < n) {
            state.panels.push({
              id: generateId(),
              query: seed
                ? JSON.parse(JSON.stringify(seed))
                : defaultFeedbackQuery(),
            })
          }
          if (!state.activePanelId) {
            state.activePanelId = state.panels[0]?.id ?? null
          }
        }),
      )
    },

    removePanelAt: (index) => {
      set(
        produce((state: KeywordsQueryState) => {
          if (state.panels.length <= 1) return
          const removed = state.panels[index]
          state.panels = state.panels.filter((_, i) => i !== index)
          if (removed) {
            delete state.ghosts[removed.id]
            delete state.shownSearch[removed.id]
          }
          if (removed && state.activePanelId === removed.id) {
            state.activePanelId = state.panels[0]?.id ?? null
          }
        }),
      )
    },

    setFilters: (panelId, filters) => {
      set(
        produce((state: KeywordsQueryState) => {
          const panel = state.panels.find((p) => p.id === panelId)
          if (panel) panel.query.filters = filters
        }),
      )
    },

    setGhosts: (panelId, ghosts) => {
      set(
        produce((state: KeywordsQueryState) => {
          if (Object.keys(ghosts).length === 0) delete state.ghosts[panelId]
          else state.ghosts[panelId] = ghosts
        }),
      )
    },

    setShownSearch: (panelId, search) => {
      set(
        produce((state: KeywordsQueryState) => {
          state.shownSearch[panelId] = search
        }),
      )
    },

    setActivePanelId: (id) => set({ activePanelId: id }),

    resetKeywordsQueries: () => set(initialKeywordsQueryState),
  }),
)
